import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid2';
import CircularProgress from '@mui/material/CircularProgress';
import backgroundImage from '../../images/home_bckg.jpg';
import { ReactComponent as ChevronDown } from '../../icons/chevron-down.svg';
import { ReactComponent as ClockIcon } from '../../icons/clock.svg';
import { ReactComponent as LocationIcon } from '../../icons/location.svg';
import { ReactComponent as VerifiedIcon } from '../../icons/verified.svg';
import Iban from './Iban';
import { useTranslation } from 'react-i18next';
import './Home.css';

const Home = () => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    apellido: '',
    nombre: '',
    presente: '',
    alergias: '',
    detalleAlergias: '',
    vegetariano: '',
    bus: '',
  });
  
  const [showBackground, setShowBackground] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const offset = 56;
  
  // Liste d'informations avec icônes et textes
  const infoItems = [
    {
      Icon: ClockIcon,
      text: (
        <div className="info-content">
          {t("home.content.2.labels.0")}
          <span style={{ color: '#575656' }}>{t("home.content.2.labels.1")}</span>
        </div>
      )
    },
    { 
      Icon: LocationIcon,
      text: t("home.content.2.labels.2")    
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      {
        threshold: 0.2,
      }
    );

    const sections = document.querySelectorAll('.section-content');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const handleScroll = () => {
    if (window.scrollY > window.innerHeight) {
      setShowBackground(false);
    } else {
      setShowBackground(true);
    }
  };

  const scrollToContent = () => {
    const contentElement = document.getElementById('content');
    if (contentElement) {
      const topPosition = contentElement.getBoundingClientRect().top + window.scrollY - offset;

      window.scrollTo({
        top: topPosition,
        behavior: 'smooth',
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const params = new URLSearchParams({
      apellido: e.target.apellido.value,
      nombre: e.target.nombre.value,
      presente: e.target.presente.value,
      alergias: e.target.alergias.value,
      detalleAlergias: e.target['detalle-alergias'].value,
      vegetariano: e.target.vegetariano.value,
      bus: e.target.bus.value,
    });

    try {
      const response = await fetch(
        `https://script.google.com/macros/s/AKfycbwwjVu6ap72VKGNCEbneZIGLkNAfWd_pAprAmqZFl_H52cS6kjwGCszfO7XCkF6KMY/exec?${params.toString()}`,
        {
          method: 'GET',
        }
      );

      if (response.ok) {
        setIsSuccess(true);
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al enviar el formulario. Por favor, inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="home-container">
      {/* Image de fond fixe */}
      {showBackground && <div className="home-background-image" style={{ backgroundImage: `url(${backgroundImage})` }}></div>}

      {/* Contenu superposé à l'image */}
      <div className="home-overlay-section">
        <h1 className="home-overlay-title" translate="no">
          Maria & Christian
        </h1>
        <h1 className="home-overlay-title" style={{fontSize: "1.8rem"}}>
          19.07.2025
        </h1>

        <div
          onClick={scrollToContent}
          className="home-scroll-indicator"
          onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.03)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
        >
          <ChevronDown className="chevron-icon" />
        </div>
      </div>

      {/* Contenu principal */}
      <div id="content">
        <div className="section section-2 center">
          <div className="section-content center">
            <div style={{maxWidth: 500, paddingTop: 100, paddingBottom: 100}}>
              <div className="section-2-heading">
                {t("home.content.1.titles.0")}<br />
                {t("home.content.1.titles.1")}
              </div>
              <div className="section-2-subtext center">
                <p className="center">
                  {t("home.content.1.labels.0")}
                </p>
                <p className="center">
                  {t("home.content.1.labels.1")}
                </p>
                {t("home.content.1.labels.2") !== "" && (
                  <p>{t("home.content.1.labels.2")}</p>
                )}
                <p className="center">
                  {t("home.content.1.labels.3")}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="section section-1">
          <div className="section-content" style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
            <h1 className="title section-1-title">
              {t("home.content.2.title")}
            </h1>

            <Grid container spacing={3} style={{maxWidth: 550, width: '100%'}}>
              {infoItems.map(({ Icon, text }, index) => (
                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                  <div className="info-item">
                    <div className="info-icon">
                      <Icon className="svg-icon" />
                    </div>
                    <p className="section-1-text info-text">{text}</p>
                  </div>
                </Grid>
              ))}
            </Grid>
          </div>
        </div>

        <div className="section section-1 form-section">
          <div className="section-content">
            <h1 className="title section-1-title">{t("home.content.3.title")}</h1>

            <form className="form" onSubmit={handleSubmit}>
              {/* Apellido */}
              <div className="form-group">
                <label htmlFor="apellido" className="form-label">
                {t("home.content.3.labels.0")} *
                </label>
                <input
                  type="text"
                  id="apellido"
                  name="apellido"
                  className="form-input"
                  placeholder={t("home.content.3.labels.1")}
                  required
                />
              </div>

              {/* Nombre */}
              <div className="form-group">
                <label htmlFor="nombre" className="form-label">
                  {t("home.content.3.labels.2")} *
                </label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  className="form-input"
                  placeholder={t("home.content.3.labels.3")}
                  required
                />
              </div>

              {/* ¿Estarás presente? */}
              <div className="form-group">
                <label className="form-label">{t("home.content.3.labels.4")} *</label>
                <div className="form-options">
                  <label className="form-option">
                    <input type="radio" name="presente" value="Sí, estaré al matrimonio." required /> {t("home.content.3.labels.5")}
                  </label>
                  <label className="form-option">
                    <input type="radio" name="presente" value="Lo siento, no puedo asistir." required /> {t("home.content.3.labels.6")}
                  </label>
                </div>
              </div>

              {/* ¿Tienes alergias alimentarias? */}
              <div className="form-group">
                <label className="form-label">{t("home.content.3.labels.7")} *</label>
                <div className="form-options">
                  <label className="form-option">
                    <input type="radio" name="alergias" value="Sí, tengo alergias." required /> {t("home.content.3.labels.8")}
                  </label>
                  <label className="form-option">
                    <input type="radio" name="alergias" value="No, no tengo alergias." required /> {t("home.content.3.labels.9")}
                  </label>
                </div>
              </div>

              {/* Si es así, por favor indícalas */}
              <div className="form-group">
                <label htmlFor="detalle-alergias" className="form-label">
                  {t("home.content.3.labels.10")}
                </label>
                <textarea
                  id="detalle-alergias"
                  name="detalle-alergias"
                  className="form-textarea"
                  rows="4"
                  placeholder={t("home.content.3.labels.11")}
                ></textarea>
              </div>

              {/* ¿Eres vegetariano? */}
              <div className="form-group">
                <label className="form-label">{t("home.content.3.labels.12")} *</label>
                <div className="form-options">
                  <label className="form-option">
                    <input type="radio" name="vegetariano" value="Sí, soy vegetariano." required /> {t("home.content.3.labels.13")}
                  </label>
                  <label className="form-option">
                    <input type="radio" name="vegetariano" value="No, no soy vegetariano." required /> {t("home.content.3.labels.14")}
                  </label>
                </div>
              </div>

              {/* ¿Necesitas transporte en bus? */}
              <div className="form-group">
                <label className="form-label">
                  {t("home.content.3.labels.15")} *
                  <br />
                  <span>{t("home.content.3.labels.22")}</span>
                </label>
                
                <div className="form-options">
                  <label className="form-option">
                    <input type="radio" name="bus" value="Sí, necesito transporte." required /> {t("home.content.3.labels.16")}
                  </label>
                  <label className="form-option">
                    <input type="radio" name="bus" value="No, no lo necesito." required /> {t("home.content.3.labels.17")}
                  </label>
                </div>
              </div>

              {/* Botón de envío */}
              <button
                type="submit"
                className={`form-button ${isSubmitting || isSuccess ? 'form-button-disabled' : ''}`}
                disabled={isSubmitting || isSuccess}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} style={{ color: '#f2f2f2' }} />
                ) : isSuccess ? (
                  <VerifiedIcon className="form-icon" />
                ) : (
                  t("home.content.3.buttons.0.label")
                )}
              </button>
            </form>

            <p>
              {t("home.content.3.labels.18")} <span style={{fontWeight: 700}}>{t("home.content.3.labels.19")}</span>
            </p>

            <p>
              {t("home.content.3.labels.20")} <span style={{fontWeight: 700}}>{t("home.content.3.labels.21")}</span>
            </p>
          </div>
        </div>

        <div className="section section-1 center">
          <div className="section-content">
            <h1 className="title section-1-title">
              {t("home.content.4.title")}
            </h1>

            <div className="section-1-text center">
              <div style={{maxWidth: 500}}>
                <p style={{marginTop: 0}}>
                  {t("home.content.4.labels.0")}
                </p>
                <p style={{marginBottom: 0}}>
                  {t("home.content.4.labels.1")}
                </p>
              </div>

              <Iban />

              <p style={{marginBottom: 0}}>
                {t("home.content.4.labels.2")}
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Home;
